// 权限配置相关类型定义

// 页面操作权限
export interface PageAction {
  key: string;
  name: string;
  hasPermission: boolean;
}

// 页面权限配置
export interface Page {
  key: string;
  name: string;
  hasPermission: boolean;
  actions?: PageAction[];
}

// 通用操作权限
export interface GenericAction {
  key: string;
  name: string;
  hasPermission: boolean;
}

// 应用配置
export interface AppConfig {
  logo_url: string;
  logo_click_url: string;
  logout_url: string;
  head_icon_url: string;
}

// 完整的权限配置
export interface PermissionConfig {
  pages: Page[];
  genericActions: GenericAction[];
  config: AppConfig;
}

// 页面键名常量
export const PAGE_KEYS = {
  HOME: 'home',
  CHARACTER: 'character', // @deprecated 已废弃 - 3D数字人
  TEMPLATE: 'template',
  SCENARIOS: 'scenarios', // @deprecated 已废弃 - 应用场景
  SUBSCRIBE: 'subscribe', // @deprecated 已废弃 - 付费订阅
  TUTORIAL: 'tutorial', // @deprecated 已废弃 - 教程
  HELP_CENTER: 'help-center', // @deprecated 已废弃 - 帮助中心
  ACADEMY: 'academy', // @deprecated 已废弃 - 魔珐学院
  RECENT_WORKS: 'recent-works', // @deprecated 已废弃 - 最近作品
  MY_COLLECTION: 'my-collection',
  PERSONAL_SPACE: 'personal-space',
  TEAM_SPACE: 'team-space',
  BRAND_MANAGE: 'brand-manage',
  CENTER_ACCOUNT: 'center-account',
  CENTER_MEMBER: 'center-member',
  CENTER_EQUITY: 'center-equity',
  CENTER_ORDER: 'center-order',
  CENTER_CONSUME: 'center-consume',
  PERSONAL_PAGE: 'personal-page',
  RESOURCE_LIBRARY: 'resource-library', // new
  LAB: 'lab', // new
  ACCOUNT_VERIFY: 'account-verify', // new
  KNOWLEDGE_ELICITATION: 'knowledge-elicitation', // new
} as const;

// 通用操作键名常量
export const GENERIC_ACTION_KEYS = {
  CLICK_LOGO_REDIRECT: 'click_logo_redirect',
  MESSAGE_CENTER_ENTRANCE: 'message_center_entrance',
  FLOAT_COMPS: 'float_comps',
  WECHAT_CONSULT: 'wechat_consult', // @deprecated 已废弃 - 微信咨询
  PHONE_CONSULT: 'phone_consult', // @deprecated 已废弃 - 电话咨询
  BOOK_DEMONSTRATION: 'book_demonstration', // @deprecated 已废弃 - 预约产品演示
  FEEDBACK_PORTAL: 'feedback_portal', // @deprecated 已废弃 - 意见反馈入口
  HUMAN_CUSTOMER_SERVICE: 'human_customer_service', // @deprecated 已废弃 - 人工客服
  INDUSTRY_APP_CASES: 'industry_app_cases', // @deprecated 已废弃 - 行业应用案例
  CREATIVE_CAMP_QR_CODE: 'creative_camp_qr_code', // @deprecated 已废弃 - 创作营二维码
  PRODUCT_UPDATE_DOC: 'product_update_doc', // @deprecated 已废弃 - 产品更新文档
  SWITCH_ACCOUNTS: 'switch_accounts',
  LOGOUT: 'logout',
  CHANGE_AVATAR: 'change_avatar',
  NEW_WORKS: 'new_works',
  NEW_WORKS_NAV: 'new_works_nav', // @deprecated 已废弃 - 新建作品_侧边导航
  CREATE_TRY_TEAM: 'create_try_team',
  PACKAGE_UPGRADE: 'package_upgrade',
  CENTER_ACCOUNT_CHANGE_VERIFY_INFO: 'center_account_change_verify_info',
  AVATAR_EDIT: 'avatar_edit', // 是否展示人物编辑的按钮
  CHENGPIAN_SHARE_TO_OUTER_USER: 'chengpian_share_to_outer_user', // 成片分享给站外用户
  CHENGPIAN_PROMOTION_PUBLISH: 'chengpian_promotion_publish', // 成片推广及发布
} as const;

// 页面键名类型
export type PageKey = (typeof PAGE_KEYS)[keyof typeof PAGE_KEYS];

// 通用操作键名类型
export type GenericActionKey = (typeof GENERIC_ACTION_KEYS)[keyof typeof GENERIC_ACTION_KEYS];

// 页面操作键名常量
export const PAGE_ACTION_KEYS = {
  // 12月2日 纯净版更新权限点
  HOME_TOP_FEATURE_TEXT2VIDEO: 'home_top_feature_text2video', // m12d2
  HOME_TOP_FEATURE_PPT2VIDEO: 'home_top_feature_ppt2video', // m12d2
  HOME_TOP_FEATURE_CUSTOM_AVATAR: 'home_top_feature_custom_avatar', // m12d2
  HOME_TOP_FEATURE_AI2PPT: 'home_top_feature_ai2ppt', // m12d2
  HOME_TOP_FEATURE_PPTSPEAK: 'home_top_feature_pptspeak', // m12d2

  // 归档权限点
  HOME_TOP: 'home_top', // @deprecated 已废弃 - 置顶
  HOME_TOP_FEATURE: 'home_top_feature',
  HOME_SEARCH: 'home_search', // @deprecated 已废弃 - 全局搜索
  HOME_CHARACTER: 'home_character', // @deprecated 已废弃 - 3D数字人
  HOME_SCENARIOS: 'home_scenarios', // @deprecated 已废弃 - 应用场景
  HOME_TEMPLATES: 'home_templates',
  HOME_RECENT_WORKS: 'home_recent_works',
  HOME_TOP_BANNER: 'home_top_banner',
  LIBRARY_CHARACTER: 'library_character',
  LIBRARY_VOICE: 'library_voice',
  LIBRARY_SCENE: 'library_scene',
  MY_COLLECTION_SOURCE_CLICK: 'my_collection_source_click', // @deprecated 已废弃，使用 my_collection_source_merge 替代
  MY_COLLECTION_SHARE_TO_OUTER_USER: 'my_collection_share_to_outer_user', // @deprecated 已废弃，使用 my_collection_source_merge 替代
  MY_COLLECTION_SOURCE_FROM: 'my_collection_source_from', // @deprecated 已废弃，使用 my_collection_source_merge 替代
  MY_COLLECTION_SOURCE_MERGE: 'my_collection_source_merge', // 来源标签合并权限点
  PERSONAL_SPACE_CHENGPIAN_PROMOTION: 'personal_space_chengpian_promotion', // @deprecated 已废弃 - 成片推广
  PERSONAL_SPACE_SHARE_TO_OUTER_USER: 'personal_space_share_to_outer_user', // @deprecated 已废弃 - 分享给站外用户
  PERSONAL_SPACE_QUEUE_ACCELERATION: 'personal_space_queue_acceleration',
  TEAM_SPACE_PUBLISH_TO_PLATFORM: 'team_space_publish_to_platform', // @deprecated 已废弃 - 发布至有言平台
  TEAM_SPACE_CHENGPIAN_PROMOTION: 'team_space_chengpian_promotion', // @deprecated 已废弃 - 成片推广
  TEAM_SPACE_SHARE_TO_OUTER_USER: 'team_space_share_to_outer_user', // @deprecated 已废弃 - 分享给站外用户
  TEAM_SPACE_CHENGPIAN_AVATAR_ROUTE: 'team_space_chengpian_avatar_route', // @deprecated 已废弃 - 成片头像跳转
  BRAND_MANAGE_FOLLOW: 'brand_manage_follow',
  CENTER_ACCOUNT_SET_ACCOUNT_INFO: 'center_account_set_account_info', // @deprecated 已废弃 - 设置账号信息
  CENTER_ACCOUNT_CHANGE_BIND_PHONE: 'center_account_change_bind_phone', // @deprecated 已废弃 - 更换绑定手机号
  CENTER_ACCOUNT_CHANGE_PASSWORD: 'center_account_change_password', // @deprecated 已废弃 - 更改密码
  CENTER_ACCOUNT_CHANGE_VERIFY_INFO: 'center_account_change_verify_info',
  CENTER_ACCOUNT_CHANGE_BASIC_INFO: 'center_account_change_basic_info', // 更改基础信息
  CENTER_ACCOUNT_BIND_ACCOUNT: 'center_account_bind_account', // 账号绑定
  CENTER_MEMBER_CHANGE_TEAM_NAME: 'center_member_change_team_name',
  CENTER_MEMBER_TRANSFER_ADMINISTRATOR: 'center_member_transfer_administrator',
  CENTER_MEMBER_LOGO_CONFIG: 'center_member_logo_config',
  CENTER_MEMBER_MANAGE_TEAM_MEMBER: 'center_member_manage_team_member',
  CENTER_MEMBER_ADD_TEAM_MEMBER_BY_NORMAL: 'center_member_add_team_member_by_normal',
  CENTER_MEMBER_ADD_TEAM_MEMBER_BY_LINK: 'center_member_add_team_member_by_link',
  CENTER_MEMBER_SHOW_TEAM_MEMBER_LOGIN_ACCOUNT: 'center_member_show_team_member_login_account',
  CENTER_MEMBER_CANCELLATION: 'center_member_cancellation',
  CENTER_MEMBER_ADD_ACCOUNT: 'center_member_add_account', // 加购席位
  CENTER_EQUITY_VIEW_EQUITY: 'center_equity_view_equity',
  CENTER_ORDER_ORDER: 'center_order_order',
  CENTER_ORDER_INVOICE: 'center_order_invoice',
  PERSONAL_PAGE_UP_AND_DOWN_SHELVES: 'personal_page_up_and_down_shelves',
  PERSONAL_PAGE_MEMBER_VIEW: 'personal_page_member_view',
} as const;

// 页面操作键名类型
export type PageActionKey = (typeof PAGE_ACTION_KEYS)[keyof typeof PAGE_ACTION_KEYS];

import { getApiClient } from '@rendora/common/services/api/index';

export const getPlatformConfig = async () => {
  const apiClient = await getApiClient();
  const res = await apiClient.get<PermissionConfig>(
    '/app_cfg/platform/',
    {},
    { autoErrorHandle: false },
  );
  return res;
};
